/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import DayPicker from "../../components/ui/datepicker/day/DayPicker";
import Input from "../../components/ui/input/Input";
import { SelectOption } from "../../components/ui/input/Input.types";
import SelectInput from "../../components/ui/input/SelectInput";
import EsimPackageSelector from "../../components/voucher-codes/EsimPackageSelector";
import useFetch from "../../hooks/useFetch";
import {
  ICreateVoucherCode,
  IEditVoucherCode,
  IVoucherCode
} from "../../interfaces/endpoints/IVoucherCode";
import {
  createVoucherCode,
  getVoucherCodeById,
  updateVoucherCode
} from "../../services/VoucherCode.service";
import VoucherCodeSchema from "../../utils/validations/VoucherCode.schema";

const APP_TZ = "Europe/Belgrade";

const rewardTypeOptions: SelectOption[] = [
  { id: 1, name: "eSIM Package" },
  { id: 2, name: "GjirafaMoney Credits" }
];

const usageModeOptions: SelectOption[] = [
  { id: 1, name: "Campaign (një përdorim për përdorues)" },
  { id: 2, name: "EmailLocked (i kyçur për një email)" }
];

type VoucherFormData = ICreateVoucherCode & { isActive?: boolean };

const AddVoucherCode: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const { state } = useLocation() as { state: { page?: number } | null };
  const navigate = useNavigate();

  const methods = useForm<VoucherFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(VoucherCodeSchema),
    defaultValues: {
      code: "",
      rewardType: undefined,
      usageMode: undefined,
      packageTemplateId: undefined,
      creditAmount: undefined,
      lockedToEmail: "",
      expiresAtUtc: "",
      isActive: true
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors }
  } = methods;

  const rewardType = watch("rewardType");
  const usageMode = watch("usageMode");
  const expiresAtUtc = watch("expiresAtUtc");

  const expiryDate = useMemo(
    () => (expiresAtUtc ? new Date(expiresAtUtc) : undefined),
    [expiresAtUtc]
  );
  const expiryTime = useMemo(
    () => (expiresAtUtc ? dayjs(expiresAtUtc).format("HH:mm") : ""),
    [expiresAtUtc]
  );

  const setExpiry = (date: Date | undefined, time: string) => {
    if (!date) {
      setValue("expiresAtUtc", "", {
        shouldValidate: true,
        shouldDirty: true
      });
      return;
    }
    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const timeStr = time || "00:00";
    setValue("expiresAtUtc", `${dateStr}T${timeStr}`, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const { response } = useFetch<IVoucherCode, number>(getVoucherCodeById, {
    initRequest: !!editMode && !!id,
    params: Number(id)
  });

  useEffect(() => {
    if (editMode && response?.data) {
      const data = response.data;
      setValue("code", data.code);
      setValue("rewardType", data.rewardType);
      setValue("usageMode", data.usageMode);
      if (data.packageTemplateId !== null && data.packageTemplateId !== undefined) {
        setValue("packageTemplateId", data.packageTemplateId);
      }
      if (data.creditAmount !== null && data.creditAmount !== undefined) {
        setValue("creditAmount", data.creditAmount);
      }
      if (data.lockedToEmail) {
        setValue("lockedToEmail", data.lockedToEmail);
      }
      if (data.expiresAtUtc) {
        setValue(
          "expiresAtUtc",
          dayjs.utc(data.expiresAtUtc).tz(APP_TZ).format("YYYY-MM-DDTHH:mm")
        );
      }
      setValue("isActive", data.isActive);
    }
  }, [editMode, response, setValue]);

  const onSubmit: SubmitHandler<VoucherFormData> = async (data) => {
    const payload = {
      code: data.code,
      rewardType: Number(data.rewardType) as 1 | 2,
      usageMode: Number(data.usageMode) as 1 | 2,
      ...(Number(data.rewardType) === 1 && {
        packageTemplateId: Number(data.packageTemplateId)
      }),
      ...(Number(data.rewardType) === 2 && {
        creditAmount: Number(data.creditAmount)
      }),
      ...(Number(data.usageMode) === 2 && {
        lockedToEmail: data.lockedToEmail
      }),
      ...(data.expiresAtUtc && {
        expiresAtUtc: dayjs.tz(data.expiresAtUtc, APP_TZ).utc().toISOString()
      }),
      isActive: data.isActive ?? true
    };

    if (editMode && id) {
      await updateVoucherCode(Number(id), payload as IEditVoucherCode)
        .then(() => {
          toast.success("Vaucheri u ndryshua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate(
              `/esim/voucher-codes?pageindex=${state?.page ? state?.page : 1}`,
              { state: ["Esim", "Voucher Codes"] }
            );
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kod ekziston tashme."
              : "Ndodhi nje gabim gjate ndryshimit.";
          toast.error(msg, { position: "top-right" });
        });
    } else {
      await createVoucherCode(payload as ICreateVoucherCode)
        .then(() => {
          toast.success("Vaucheri u shtua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate("/esim/voucher-codes", {
              state: ["Esim", "Voucher Codes"]
            });
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kod ekziston tashme."
              : "Ndodhi nje gabim gjate krijimit.";
          toast.error(msg, { position: "top-right" });
        });
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim/voucher-codes"
        state={["Esim", "Voucher Codes"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko vaucherin" : "Shto vaucher te ri"}
      >
        <Input
          {...register("code")}
          label="Kodi i vaucherit"
          className="mb-6"
          placeholder="p.sh. WELCOME5EUR"
          error={errors.code?.message}
        />

        <div className="mb-6">
          <SelectInput
            label="Lloji i shpërblimit"
            placeholder="Zgjidh llojin"
            options={rewardTypeOptions}
            selectedId={rewardType ?? ""}
            onOptionSelect={(opt) =>
              setValue(
                "rewardType",
                opt ? (Number(opt.id) as 1 | 2) : (undefined as unknown as 1 | 2),
                { shouldValidate: true, shouldDirty: true }
              )
            }
            error={errors.rewardType?.message}
          />
        </div>

        <div className="mb-6">
          <SelectInput
            label="Mënyra e përdorimit"
            placeholder="Zgjidh mënyrën"
            options={usageModeOptions}
            selectedId={usageMode ?? ""}
            onOptionSelect={(opt) =>
              setValue(
                "usageMode",
                opt ? (Number(opt.id) as 1 | 2) : (undefined as unknown as 1 | 2),
                { shouldValidate: true, shouldDirty: true }
              )
            }
            error={errors.usageMode?.message}
          />
        </div>

        {Number(rewardType) === 1 && (
          <EsimPackageSelector
            value={watch("packageTemplateId")}
            onChange={(packageId) => {
              setValue(
                "packageTemplateId",
                packageId as unknown as number,
                {
                  shouldValidate: true,
                  shouldDirty: true
                }
              );
            }}
            error={errors.packageTemplateId?.message}
          />
        )}

        {Number(rewardType) === 2 && (
          <Input
            {...register("creditAmount")}
            label="Shuma e kreditit (EUR)"
            className="mb-6"
            type="number"
            step="0.01"
            placeholder="p.sh. 5.00"
            error={errors.creditAmount?.message}
          />
        )}

        {Number(usageMode) === 2 && (
          <Input
            {...register("lockedToEmail")}
            label="Email (i kycur)"
            className="mb-6"
            type="email"
            placeholder="user@example.com"
            error={errors.lockedToEmail?.message}
          />
        )}

        <input type="hidden" {...register("expiresAtUtc")} />

        <div className="mb-6">
          <div className="flex items-end justify-between mb-2">
            <span className="text-sm text-tableColor">
              Skadimi (opsionale)
            </span>
            {expiresAtUtc && (
              <button
                type="button"
                onClick={() => setExpiry(undefined, "")}
                className="text-xs text-gray-500 hover:text-primary underline transition-colors"
              >
                Pastro
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <DayPicker
                label="Data e skadimit"
                placeholder="Zgjidh datën"
                date={expiryDate}
                onChangeDate={(d) => setExpiry(d, expiryTime)}
                iconSide="right"
                iconColor="secondary"
                onlyModal
              />
            </div>
            <Input
              type="time"
              label="Ora (HH:mm)"
              value={expiryTime}
              onChange={(e) => setExpiry(expiryDate, e.target.value)}
              placeholder="HH:mm"
              disabled={!expiryDate}
              error={errors.expiresAtUtc?.message}
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("isActive")}
              className="mr-2 w-4 h-4"
            />
            <span className="text-sm font-medium">Aktiv</span>
          </label>
        </div>
      </FormWrapper>
    </FormProvider>
  );
};

export default AddVoucherCode;
